import { STATEMENT_BASIS_LABELS, STATEMENT_BASIS_OPTIONS } from './statement-basis';
import { describePeriodKey } from './period-tree';
import { inFolder, inFolderPeriod } from './category-tree';
import type { RoomDocument, StatementBasis } from '../api/types';
import type { ActiveFilterChip } from '../ui/active-filters-bar';

/**
 * Everything that narrows the Data room's document list (owner ask 2026-10-15:
 * "once I select a filter … there is not an obvious way to go back"). It lives
 * in the URL's query string, so the browser's Back button returns to the view
 * before a filter was applied, a filtered view can be linked to, and the plain
 * `/rooms/:roomId` link everything else uses is always the unfiltered main view.
 *
 *   phase   — a checklist phase row in the tree (a phase id, or `other`): the
 *             documents under its top-level categories. A folder replaces it.
 *   folder  — the selected folder (a category id) in the tree
 *   period  — a Year / Month / Quarter / Undated node under that folder
 *   q       — the search text
 *   show    — `needs-look` (low confidence + couldn't file) or `unreadable`
 *   basis   — a financial statement's accounting basis
 *
 * The admin's Data room and the seller's "Your documents" share it (owner,
 * 2026-10-15: "The seller's document area should look just like the admin's").
 * `needs-look` is the admin's alone — the seller's page never offers it.
 * The list's sort (`sort` / `dir`, lib/document-sort.ts) is not a filter and is
 * left untouched here.
 */
export type DataRoomShow = 'needs-look' | 'unreadable';

export interface DataRoomFilters {
  phase: string | null;
  folder: string | null;
  period: string | null;
  query: string;
  show: DataRoomShow | null;
  basis: StatementBasis | null;
}

export const NO_DATA_ROOM_FILTERS: DataRoomFilters = {
  phase: null,
  folder: null,
  period: null,
  query: '',
  show: null,
  basis: null,
};

const KEYS = ['phase', 'folder', 'period', 'q', 'show', 'basis'] as const;
const SHOWS: readonly DataRoomShow[] = ['needs-look', 'unreadable'];

/** Read the filters from a query string; anything unrecognised is ignored. */
export function dataRoomFiltersFrom(search: URLSearchParams): DataRoomFilters {
  const folder = search.get('folder')?.trim() || null;
  // A phase and a folder never narrow together: the folder is the finer pick.
  const phase = folder ? null : search.get('phase')?.trim() || null;
  const periodRaw = search.get('period');
  // A period is a level UNDER a folder: without one it means nothing.
  const period = folder && periodRaw && describePeriodKey(periodRaw) ? periodRaw : null;
  const showRaw = search.get('show');
  const basisRaw = search.get('basis');
  return {
    phase,
    folder,
    period,
    query: (search.get('q') ?? '').trim(),
    show: SHOWS.find((s) => s === showRaw) ?? null,
    basis: STATEMENT_BASIS_OPTIONS.find((b) => b === basisRaw) ?? null,
  };
}

/**
 * The query string for `filters`, keeping any parameter this page does not own.
 * The unfiltered view writes none of its own keys, so it is the bare room URL.
 */
export function dataRoomSearch(
  filters: DataRoomFilters,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const k of KEYS) next.delete(k);
  if (filters.phase && !filters.folder) next.set('phase', filters.phase);
  if (filters.folder) {
    next.set('folder', filters.folder);
    if (filters.period) next.set('period', filters.period);
  }
  if (filters.query) next.set('q', filters.query);
  if (filters.show) next.set('show', filters.show);
  if (filters.basis) next.set('basis', filters.basis);
  return next;
}

/** True when anything narrows the list. */
export function hasDataRoomFilters(filters: DataRoomFilters): boolean {
  return Boolean(
    filters.phase ||
      filters.folder ||
      filters.period ||
      filters.query ||
      filters.show ||
      filters.basis,
  );
}

/** What a "show" filter is called on its chip. */
export const DATA_ROOM_SHOW_LABELS: Record<DataRoomShow, string> = {
  'needs-look': 'Needs a look',
  unreadable: 'Couldn’t read',
};

/**
 * One removable chip per filter applied, in the "Showing:" bar's order: the
 * phase, the folder (and its period), the search text, the "show" filter, the
 * basis. `remove(patch)` is what each chip's ✕ does. `folderName` /
 * `periodLabel` / `phaseName` are the selected folder's name, period's full
 * label and phase's name, as the tree shows them.
 */
export function dataRoomFilterChips(
  filters: DataRoomFilters,
  names: { folderName: string; periodLabel: string | null; phaseName?: string | null },
  remove: (patch: Partial<DataRoomFilters>) => void,
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (filters.phase && !filters.folder) {
    chips.push({
      key: 'phase',
      label: `Phase: ${names.phaseName ?? 'A phase'}`,
      onRemove: () => remove({ phase: null }),
    });
  }
  if (filters.folder) {
    chips.push({
      key: 'folder',
      label: `Folder: ${names.folderName}`,
      onRemove: () => remove({ folder: null, period: null }),
    });
    if (names.periodLabel) {
      chips.push({
        key: 'period',
        label: names.periodLabel,
        onRemove: () => remove({ period: null }),
      });
    }
  }
  if (filters.query) {
    chips.push({
      key: 'q',
      label: `Search: “${filters.query}”`,
      onRemove: () => remove({ query: '' }),
    });
  }
  if (filters.show) {
    chips.push({
      key: 'show',
      label: DATA_ROOM_SHOW_LABELS[filters.show],
      onRemove: () => remove({ show: null }),
    });
  }
  if (filters.basis) {
    chips.push({
      key: 'basis',
      label: `${STATEMENT_BASIS_LABELS[filters.basis]} basis`,
      onRemove: () => remove({ basis: null }),
    });
  }
  return chips;
}

/**
 * The list the filters leave, from what the listing (or the text search) `found`:
 * `listed` — narrowed to the selected folder (with its period node) or phase, by
 * the tree's own rule (`inFolder`), so a folder lists exactly its count — never
 * the server's "carries this tag" facet, which also matches a document's edges
 * on other branches; `visible` — then by the "show" filter
 * and the basis. Search hits carry no tags, so every test reads the polled
 * listing's copy (`live`). `needsLook` decides the admin's "Needs a look"
 * filter; `inPhase` the selected phase (absent: the phase is not narrowed).
 */
export function narrowDataRoomDocuments<T extends RoomDocument>(
  found: readonly T[],
  filters: DataRoomFilters,
  ctx: {
    live: (doc: T) => RoomDocument | undefined;
    parentOf: ReadonlyMap<string, string | null>;
    needsLook?: (doc: T) => boolean;
    inPhase?: (doc: RoomDocument) => boolean;
  },
): { listed: T[]; visible: T[] } {
  const inPhase = ctx.inPhase;
  const listed = filters.folder
    ? found.filter((d) => {
        const live = ctx.live(d);
        if (!live) return false;
        const folder = filters.folder as string;
        return filters.period
          ? inFolderPeriod(live, folder, filters.period, ctx.parentOf)
          : inFolder(live, folder, ctx.parentOf);
      })
    : filters.phase && inPhase
      ? found.filter((d) => {
          const live = ctx.live(d);
          return live ? inPhase(live) : false;
        })
      : [...found];
  const shown =
    filters.show === 'needs-look'
      ? listed.filter((d) => ctx.needsLook?.(d) ?? false)
      : filters.show === 'unreadable'
        ? listed.filter((d) => Boolean((ctx.live(d) ?? d).unreadable))
        : listed;
  const visible = filters.basis
    ? shown.filter((d) => (ctx.live(d) ?? d).statementBasis?.basis === filters.basis)
    : shown;
  return { listed, visible };
}
